import axios, { isCancel, AxiosHeaders, isAxiosError } from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';

import { APP_ENV } from '@/config/env';
import { triggerLogout } from '@/services/auth-session';
import { logger } from '@/services/logger.service';
import TokenService from '@/services/token.service';

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
    _retry?: boolean;
    skipErrorReporting?: boolean;
}

interface FailedRequest {
    resolve: (token: string | null) => void;
    reject: (error: AxiosError) => void;
}

/** Auth routes that must not trigger access-token refresh on 401 */
const PUBLIC_AUTH_PATHS = [
    '/auth/login',
    '/auth/login/mfa',
    '/auth/confirm/mfa',
    '/auth/resend-code',
    '/auth/password/forgot',
    '/auth/password/reset'
] as const;

const isPublicAuthRequest = (url: string | undefined): boolean => {
    if (!url) {
        return false;
    }
    return PUBLIC_AUTH_PATHS.some((path) => url.includes(path));
};

const axiosInstance = axios.create({
    baseURL: APP_ENV.VITE_API_BASE_URL || '/api',
    timeout: 30_000,
    headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true'
    }
});

// Request Interceptor
axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Rely solely on TokenService as the source of truth for the token string
        const token = TokenService.getAccessToken();

        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error: AxiosError) => Promise.reject(error)
);

// We need an array and a boolean to prevent multiple refresh calls simultaneously
let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const shouldReportAxiosError = (error: AxiosError, config: CustomAxiosRequestConfig): boolean => {
    if (config.skipErrorReporting) {
        return false;
    }

    if (axios.isCancel(error)) {
        return false;
    }

    const status = error.response?.status;
    if (status === 401) {
        return false;
    }

    return true;
};

const reportAxiosError = (error: AxiosError, config?: CustomAxiosRequestConfig): void => {
    if (!config || !shouldReportAxiosError(error, config)) {
        return;
    }
    const { status } = error.response || {};
    if (typeof status === 'number') {
        //const message = (data as Record<string, unknown>)?.message || '';
        if (status === 403 && !isPublicAuthRequest(config.url)) {
            // TODO: add message to toast
            toast.error('You do not have permission to perform this action.');
        } else if (status >= 500) {
            toast.error('Something went wrong on our side. Please try again.');
        }
    }

    logger.error('API request failed', error, {
        url: config.url,
        method: config.method,
        status,
        statusText: error.response?.statusText
    });
};

const rejectAxiosError = (error: AxiosError, config?: CustomAxiosRequestConfig): Promise<never> => {
    reportAxiosError(error, config);
    return Promise.reject(error);
};

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as CustomAxiosRequestConfig;

        if (!originalRequest) {
            return rejectAxiosError(error);
        }

        // Check if error is 401 Unauthorized and we haven't already retried this original request
        const isRevokedSession = error?.response?.status === 401 && !isPublicAuthRequest(originalRequest.url);
        const shouldAttemptRefresh = isRevokedSession && !originalRequest._retry && Boolean(TokenService.getRefreshToken());

        if (shouldAttemptRefresh) {
            // If we are already refreshing the token, simply add this original request to the queue
            if (isRefreshing) {
                return new Promise<string | null>((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                        }
                        return axiosInstance(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Attempt to get a new token via our TokenService
                const newAccessToken = await TokenService.refreshAccessToken();

                // Processing failed requests queue
                processQueue(null, newAccessToken);

                // Execute the original request that failed
                if (originalRequest.headers && newAccessToken) {
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                }
                return await axiosInstance(originalRequest);
            } catch (refreshError: unknown) {
                // Refresh token failed = force logout
                const axiosRefreshError = refreshError as AxiosError;
                processQueue(axiosRefreshError, null);
                await triggerLogout();
                TokenService.clearTokens();

                return await Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Deactivated or otherwise revoked sessions cannot be refreshed. Send the user back to login.
        if (isRevokedSession) {
            await triggerLogout();
            TokenService.clearTokens();
        }

        return rejectAxiosError(error, originalRequest);
    }
);

export default axiosInstance;
export { isCancel, isAxiosError, AxiosHeaders };
export type { AxiosError, InternalAxiosRequestConfig };
